<template>
    <v-group>
        <v-arrow v-if="configImaginaryEdge" :config="configImaginaryEdge.arrowConfig" />
        <KonvaCampaignImage v-if="configImaginaryEdge" :src="imagePath.pointerImage" :config="configImaginaryEdge.pointerConfig" />

        <v-group
            __use-strict-mode
            data-qa="konva-automation-inline-add"
            :config="configContainerOffset"
        >
            <v-rect :config="configRectContainer" />

            <v-group
                v-if="canAddWhen"
                @mouseenter="onMouseEnterWhen"
                @mouseleave="onMouseExit"
                @click="onClickAddWhen"
            >
                <v-rect :config="configAddWhen" />

                <template v-if="showBigCard">
                    <v-text :config="configAddWhenText" />

                    <v-text :config="configAddWhenSubtext" />
                </template>

                <KonvaCampaignImage :src="imagePath.greenCirclePlus" :config="configCircleIcon" />
            </v-group>

            <v-group
                v-if="canAddThen"
                :config="{ y: canAddWhen && $options.ICON_SIZE || 0 }"
                @mouseenter="onMouseEnterThen"
                @mouseleave="onMouseExit"
                @click="onClickAddThen"
            >
                <v-rect :config="configAddThen" />

                <template v-if="showBigCard">
                    <v-text :config="configAddThenText" />

                    <v-text :config="configAddThenSubtext" />
                </template>

                <KonvaCampaignImage :src="imagePath.blueCirclePlus" :config="configCircleIconThen" />
            </v-group>
        </v-group>
    </v-group>
</template>

<script>
import {
    PROCESSES,
    SEQUENCE_START,
    TIMERS,
    COMMUNICATIONS,
    GOALS,
    FLOW_TYPE,
    GRID_SIZE,
    CELL_TYPE,
    INTEGRATION_TRIGGER_TYPE,
    INTEGRATION_ACTION_TYPE,
} from '../utils/editor.constants';
import KonvaCampaignImage from './KonvaCampaignImage.vue';
import { setCursor } from '../utils/cell/automation-display-utils';

const ICON_SIZE = GRID_SIZE * 0.75;
const CONTAINER_PADDING = 12;

export default {
    ICON_SIZE,
    CONTAINER_PADDING,
    components: {
        KonvaCampaignImage,
    },
    props: {
        isFirstTimeUse: {
            type: Boolean,
            default: () => false,
        },
        showBigCard: {
            type: Boolean,
            default: () => false,
        },
        isInlineAddOpen: {
            type: Boolean,
            default: () => false,
        },
        sourceElement: {
            type: Object,
            default: () => null,
        },
    },
    emits: ['open-inline-add', 'close-inline-add'],
    data() {
        return {
            isHoveringWhen: false,
            isHoveringThen: false,
            isWhenMenuOpen: false,
            isThenMenuOpen: false,
            imagePath: {
                greenCirclePlus: '/resources/funnel/images/svg/cell-icon/green/circlePlus.svg',
                blueCirclePlus: '/resources/funnel/images/svg/cell-icon/blue/circlePlus.svg',
                pointerImage: '/resources/funnel/images/svg/connectors/gray.svg',
            },
        };
    },

    computed: {
        configImaginaryEdge() {
            if (!this.sourceElement) return null;

            const points = [{
                x: this.sourceElement.mxGeometry.x + GRID_SIZE + 6,
                y: this.sourceElement.mxGeometry.y + (GRID_SIZE / 2),
            }, {
                x: this.sourceElement.mxGeometry.x + GRID_SIZE * 6 - 30,
                y: this.sourceElement.mxGeometry.y + (GRID_SIZE / 2),
            }];

            const pointerConfig = { x: points[1].x - 5, y: points[1].y - 16 };
            const arrowConfig = {
                points: [points[0].x, points[0].y, points[1].x, points[1].y],
                fill: '#ddd',
                stroke: '#ddd',
                strokeWidth: 2,
                pointerAtEnding: false,
            };

            return { pointerConfig, arrowConfig };
        },

        configContainerOffset() {
            if (this.isFirstTimeUse) {
                return {
                    x: GRID_SIZE * 2,
                    y: GRID_SIZE * 2,
                };
            }

            const xOffset = GRID_SIZE * 5;
            const yOffset = this.canAddThen && this.canAddWhen
                ? -(ICON_SIZE / 2)
                : 0;

            const coords = {
                x: this.sourceElement.mxGeometry.x + xOffset,
                y: this.sourceElement.mxGeometry.y + yOffset,
            };

            return coords;
        },

        configRectContainer() {
            const width = this.showBigCard
                ? GRID_SIZE * 4.25
                : GRID_SIZE;

            const height = this.showBigCard && this.canAddWhen && this.canAddThen
                ? GRID_SIZE * 2
                : this.canAddWhen && this.canAddThen
                    ? (ICON_SIZE * 2) + CONTAINER_PADDING
                    : this.showBigCard && this.canAddThen
                        ? (CONTAINER_PADDING * 4)
                        : GRID_SIZE;

            return {
                width,
                height,
                cornerRadius: 12,
                fill: 'white',
                strokeWidth: 3,
                stroke: '#ddd',
                dash: [6, 6],
            };
        },

        configCircleIcon() {
            return {
                x: CONTAINER_PADDING / 2,
                y: CONTAINER_PADDING / 2,
                width: ICON_SIZE,
                height: ICON_SIZE,
            };
        },

        configCircleIconThen() {
            return {
                x: CONTAINER_PADDING / 2,
                y: this.showBigCard && this.canAddWhen
                    ? (CONTAINER_PADDING / 2) + 4
                    : CONTAINER_PADDING / 2,
                width: ICON_SIZE,
                height: ICON_SIZE,
            };
        },

        configAddWhenText() {
            return {
                x: GRID_SIZE,
                y: CONTAINER_PADDING * 0.75,
                text: this.isFirstTimeUse
                    ? this.$t('funnel.inlineAdd.addWhen.firstTime')
                    : this.$t('funnel.inlineAdd.addWhen'),
                fontStyle: 'bold',
                fontFamily: 'Sul Sans',
                fontSize: 14,
                align: 'left',
                verticalAlign: 'middle',
            };
        },

        configAddWhenSubtext() {
            return {
                x: GRID_SIZE,
                y: CONTAINER_PADDING * 1.4,
                width: 200,
                height: 30,
                text: this.isFirstTimeUse
                    ? this.$t('funnel.inlineAdd.addWhenSubtext.firstTime')
                    : this.$t('funnel.inlineAdd.addWhenSubtext'),
                fontFamily: 'Sul Sans',
                fontSize: this.isFirstTimeUse
                    ? 11
                    : 10,
                fill: '#000',
                align: 'left',
                verticalAlign: 'middle',
            };
        },

        configAddThenText() {
            return {
                x: GRID_SIZE,
                y: this.showBigCard && this.canAddWhen
                    ? CONTAINER_PADDING * 1.2
                    : CONTAINER_PADDING * 0.75,
                text: this.$t('funnel.inlineAdd.addThen'),
                fontStyle: 'bold',
                fontFamily: 'Sul Sans',
                fontSize: 14,
                fill: '#000',
                align: 'left',
                verticalAlign: 'middle',
            };
        },

        configAddThenSubtext() {
            return {
                x: GRID_SIZE,
                y: this.showBigCard && this.canAddWhen
                    ? CONTAINER_PADDING * 2
                    : CONTAINER_PADDING * 1.4,
                width: 200,
                height: 30,
                text: this.$t('funnel.inlineAdd.addThenSubtext'),
                fontFamily: 'Sul Sans',
                fontSize: 10,
                fill: '#000',
                align: 'left',
                verticalAlign: 'middle',
            };
        },

        configAddWhen() {
            if (!this.canAddWhen) return {};

            return {
                width: this.showBigCard ? GRID_SIZE * 4 : ICON_SIZE,
                height: this.showBigCard ? ICON_SIZE + 8 : ICON_SIZE,
                cornerRadius: 12,
                x: CONTAINER_PADDING / 2,
                y: this.showBigCard ? 2 : CONTAINER_PADDING / 2,
                fill: this.isHoveringWhen ? '#e8f6f8' : 'transparent',
            };
        },

        configAddThen() {
            if (!this.canAddThen) return {};

            return {
                width: this.showBigCard ? GRID_SIZE * 4 : ICON_SIZE,
                height: this.showBigCard ? ICON_SIZE + 8 : ICON_SIZE,
                cornerRadius: 12,
                x: CONTAINER_PADDING / 2,
                y: this.showBigCard && this.canAddWhen
                    ? (CONTAINER_PADDING / 2) + 4
                    : this.showBigCard ? 2 : CONTAINER_PADDING / 2,
                fill: this.isHoveringThen ? '#D6F0FF' : 'transparent',
            };
        },

        canAddThen() {
            return this.sourceElementIsWhen || this.sourceElementIsThen || this.sourceElementIsSequence;
        },

        canAddWhen() {
            return this.isFirstTimeUse || this.sourceElementIsSequence;
        },

        sourceElementIsSequence() {
            return this.sourceElement?.type === FLOW_TYPE;
        },

        sourceElementIsWhen() {
            return Object.values([INTEGRATION_TRIGGER_TYPE, ...Object.values(GOALS)]).includes(this.sourceElement?.type);
        },

        sourceElementIsThen() {
            return [INTEGRATION_ACTION_TYPE, SEQUENCE_START, ...Object.values({ ...PROCESSES, ...TIMERS, ...COMMUNICATIONS })]
                .includes(this.sourceElement?.type);
        },
    },

    methods: {
        onMouseEnterWhen(event) {
            if (!this.canAddWhen) return;

            setCursor(event, 'pointer');
            this.isHoveringWhen = true;
        },

        onMouseEnterThen(event) {
            if (!this.canAddThen) return;

            setCursor(event, 'pointer');

            this.isHoveringThen = true;
        },

        onMouseExit(event) {
            setCursor(event, 'default');
            this.isHoveringWhen = false;
            this.isHoveringThen = false;
        },

        onClickAddWhen({ evt }) {
            const shouldCloseMenu = (this.isWhenMenuOpen && this.isInlineAddOpen && !this.isThenMenuOpen);
            const eventType = shouldCloseMenu ? 'close-inline-add' : 'open-inline-add';

            this.isWhenMenuOpen = !shouldCloseMenu;
            this.isThenMenuOpen = false;
            this.$emit(eventType, {
                sourceElement: this.sourceElement,
                showLabels: this.showBigCard,
                type: CELL_TYPE.WHEN,
                event: evt,
            });
        },

        onClickAddThen({ evt }) {
            const shouldCloseMenu = (this.isThenMenuOpen && this.isInlineAddOpen && !this.isWhenMenuOpen);
            const eventType = shouldCloseMenu ? 'close-inline-add' : 'open-inline-add';

            this.isThenMenuOpen = !shouldCloseMenu;
            this.isWhenMenuOpen = false;
            this.$emit(eventType, {
                sourceElement: this.sourceElement,
                showLabels: this.showBigCard,
                type: CELL_TYPE.THEN,
                event: evt,
            });
        },
    },
};
</script>
